import type { PublicEchoFeedbackRecord } from "@/server/echoFeedback";

export const FEEDBACK_TIMESTAMP_QUERY_PARAM = "feedbackTimestampMs";

const MAX_FEEDBACK_TIMESTAMP_MS = 86_400_000;

export function buildFeedbackEchoHref(record: PublicEchoFeedbackRecord) {
  const search = new URLSearchParams({
    feedbackId: record.id,
    [FEEDBACK_TIMESTAMP_QUERY_PARAM]: String(record.position.timestampMs),
  });
  return `/echoflicks_echoes/${encodeURIComponent(record.echo.id)}/show?${search}#echo-final-video`;
}

export function parseFeedbackTimestampMs(search: URLSearchParams) {
  const raw = search.get(FEEDBACK_TIMESTAMP_QUERY_PARAM);
  if (raw === null || raw.trim() === "") return null;

  const timestampMs = Number(raw);
  if (
    !Number.isSafeInteger(timestampMs) ||
    timestampMs < 0 ||
    timestampMs > MAX_FEEDBACK_TIMESTAMP_MS
  ) {
    return null;
  }
  return timestampMs;
}

export function seekVideoToFeedbackTimestamp(
  video: HTMLVideoElement,
  timestampMs: number,
) {
  if (!Number.isFinite(timestampMs) || timestampMs < 0) return false;

  const requestedSeconds = timestampMs / 1_000;
  const duration =
    Number.isFinite(video.duration) && video.duration >= 0
      ? video.duration
      : null;
  const targetSeconds =
    duration === null ? requestedSeconds : Math.min(requestedSeconds, duration);

  try {
    video.currentTime = targetSeconds;
    return true;
  } catch {
    return false;
  }
}

export function seekVideoToFeedbackTimestampWhenReady(
  video: HTMLVideoElement,
  timestampMs: number,
  onSeek?: () => void,
) {
  const seek = () => {
    if (seekVideoToFeedbackTimestamp(video, timestampMs)) onSeek?.();
  };

  if (video.readyState >= 1) {
    seek();
    return () => undefined;
  }

  video.addEventListener("loadedmetadata", seek, { once: true });
  return () => video.removeEventListener("loadedmetadata", seek);
}
